import { useTranslation } from 'react-i18next';
import { Group, Input, MultiSelect, NumberInput, Radio, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useTransportSurcharges } from '@/hooks/useBestOffers';
import { addDaysIso, tomorrowIso } from '@/lib/date';
import {
  RATE_MAX,
  RATE_TON_MIN,
  RATE_TS_MIN,
  SCALES_MAX,
  SCALES_MIN,
  STORAGE_PRICE_MIN,
  VEHICLE_HEIGHT_MAX,
  VEHICLE_HEIGHT_MIN,
  type SellFormState,
} from './sellFormState';

interface LoadingConditionsFieldsProps {
  readonly state: SellFormState;
  readonly set: (patch: Partial<SellFormState>) => void;
  readonly shipmentModes: string[];
  /** Ошибки дат (i18n-ключи), вычислены в форме (зависят от объёма). */
  readonly dateErrors: { startDate?: string; endDate?: string };
}

// Условия погрузки и хранения. Контролируемый компонент над срезом состояния формы.
// Раздел разбит на безрамочные подгруппы (Сроки / Отгрузка / Оборудование / Хранение): каждый управляющий
// контрол стоит рядом со своим полем (гештальт-близость), выбор режима — Radio (все опции видны), бинарные
// «да/нет» — Switch. Даты — нативный type="date" (ISO); время — type="time" (HH:mm).
export default function LoadingConditionsFields({ state, set, shipmentModes, dateErrors }: LoadingConditionsFieldsProps) {
  const { t } = useTranslation();
  // Ставки надбавок — для подписей «выключение +X ₽/т·км». Пока не загружены, подписи не показываем.
  const { data: surcharges } = useTransportSurcharges();
  const exact = state.startMode === 'EXACT_DATE';
  const minStart = tomorrowIso();
  const minEnd = state.startDate ? addDaysIso(state.startDate, 1) : minStart;

  // Подгруппы без заголовков — разделяются увеличенным вертикальным отступом; внутри группы отступ мельче
  // (правило близости: связь ближе разделения).
  return (
    <Stack gap={40}>
      {/* Подгруппа: сроки погрузки */}
      <Stack gap="sm">
        <Radio.Group
          label={t('app.sell.form.loading.startMode')}
          value={state.startMode}
          onChange={(value) => set({ startMode: value as SellFormState['startMode'] })}
        >
          <Group gap="lg" mt={6}>
            <Radio value="NO_DATE" label={t('app.sell.form.loading.modeNoDate')} />
            <Radio value="EXACT_DATE" label={t('app.sell.form.loading.modeExact')} />
            <Radio value="N_DAYS_AFTER_PAYMENT" label={t('app.sell.form.loading.modeAfterPayment')} />
          </Group>
        </Radio.Group>

        {exact ? (
          <Group grow align="flex-start" wrap="wrap">
            <TextInput
              type="date"
              label={t('app.sell.form.loading.startDate')}
              value={state.startDate}
              min={minStart}
              onChange={(e) => set({ startDate: e.currentTarget.value })}
              error={dateErrors.startDate ? t(dateErrors.startDate) : undefined}
            />
            <TextInput
              type="date"
              label={t('app.sell.form.loading.endDate')}
              value={state.endDate}
              min={minEnd}
              onChange={(e) => set({ endDate: e.currentTarget.value })}
              error={dateErrors.endDate ? t(dateErrors.endDate) : undefined}
            />
          </Group>
        ) : null}

        <Group grow align="flex-start" wrap="wrap">
          <TextInput
            type="time"
            label={t('app.sell.form.loading.startTime')}
            value={state.startTime}
            onChange={(e) => set({ startTime: e.currentTarget.value })}
          />
          <TextInput
            type="time"
            label={t('app.sell.form.loading.endTime')}
            value={state.endTime}
            onChange={(e) => set({ endTime: e.currentTarget.value })}
          />
        </Group>
      </Stack>

      {/* Подгруппа: отгрузка */}
      <Stack gap="sm">
        <MultiSelect
          label={t('app.sell.form.loading.modes')}
          placeholder={state.shipmentModes.length ? undefined : t('app.sell.form.loading.modesPlaceholder')}
          data={shipmentModes}
          value={state.shipmentModes}
          onChange={(value) => set({ shipmentModes: value })}
          searchable
        />

        <Group grow align="flex-start" wrap="wrap">
          {/* Суточная отгрузка — одно поле «значение + единица»: единица компактным сегментом справа от числа. */}
          <Input.Wrapper label={t('app.sell.form.loading.rateValue')}>
            <Group gap="xs" wrap="nowrap" align="center" mt={4}>
              <NumberInput
                aria-label={t('app.sell.form.loading.rateValue')}
                value={state.rate}
                onChange={(value) => set({ rate: value === '' ? '' : Number(value) })}
                min={state.rateType === 'TS' ? RATE_TS_MIN : RATE_TON_MIN}
                max={RATE_MAX}
                allowNegative={false}
                decimalScale={state.rateType === 'TS' ? 0 : 1}
                clampBehavior="blur"
                style={{ flex: 1, minWidth: 0 }}
              />
              <Radio.Group
                value={state.rateType}
                onChange={(value) => set({ rateType: value as SellFormState['rateType'] })}
                aria-label={t('app.sell.form.loading.rateType')}
              >
                <Group gap="md" wrap="nowrap">
                  <Radio value="ton" label={t('app.sell.form.loading.rateTon')} />
                  <Radio value="TS" label={t('app.sell.form.loading.rateTS')} />
                </Group>
              </Radio.Group>
            </Group>
          </Input.Wrapper>
          <NumberInput
            label={t('app.sell.form.loading.vehicleHeight')}
            value={state.vehicleHeight}
            onChange={(value) => set({ vehicleHeight: value === '' ? '' : Number(value) })}
            min={VEHICLE_HEIGHT_MIN}
            max={VEHICLE_HEIGHT_MAX}
            allowNegative={false}
            decimalScale={1}
            clampBehavior="blur"
          />
        </Group>
      </Stack>

      {/* Подгруппа: оборудование базиса (влияет на надбавки к перевозке и ориентир цены выше).
          По умолчанию всё включено — надбавок нет; выключение опции добавляет надбавку и снижает лучшую цену. */}
      <Stack gap="sm">
        {/* Весы + длина на одной строке. Длина всегда отрисована, но НЕАКТИВНА при выключенных весах —
            так разметка не прыгает (как отключённое поле «Качество» до выбора культуры). */}
        <Group align="center" wrap="wrap" gap="xl">
          <Switch
            checked={state.hasScales}
            onChange={(e) => set({ hasScales: e.currentTarget.checked })}
            label={t('app.sell.form.loading.scalesSwitch')}
            description={surcharges ? t('app.sell.form.loading.surchargeScales', { rate: surcharges.noScalesRate }) : undefined}
            styles={{ description: { color: 'var(--mantine-color-gray-7)' } }}
          />
          {/* Подпись поля — инлайн (слева от ввода): поле в одну строку, его ось совпадает с осью тумблера. */}
          <Group gap="xs" align="center" wrap="nowrap">
            <Text size="sm" c={state.hasScales ? undefined : 'dimmed'}>
              {t('app.sell.form.loading.scalesLength')}
            </Text>
            <NumberInput
              aria-label={t('app.sell.form.loading.scalesLength')}
              value={state.scalesLength}
              onChange={(value) => set({ scalesLength: value === '' ? '' : Number(value) })}
              disabled={!state.hasScales}
              min={SCALES_MIN}
              max={SCALES_MAX}
              allowNegative={false}
              decimalScale={1}
              clampBehavior="blur"
              w={110}
            />
          </Group>
        </Group>
        <Switch
          checked={state.trawls}
          onChange={(e) => set({ trawls: e.currentTarget.checked })}
          label={t('app.sell.form.loading.trawls')}
          description={
            surcharges
              ? t('app.sell.form.loading.surchargeTrawls', { rate: surcharges.noTralRate, km: surcharges.minDistanceKm })
              : undefined
          }
          styles={{ description: { color: 'var(--mantine-color-gray-7)' } }}
        />
        <Switch
          checked={state.trailers}
          onChange={(e) => set({ trailers: e.currentTarget.checked })}
          label={t('app.sell.form.loading.trailers')}
          description={
            surcharges
              ? t('app.sell.form.loading.surchargeTrailers', {
                  rate: surcharges.noDumpTruckRate,
                  km: surcharges.minDistanceKm,
                })
              : undefined
          }
          styles={{ description: { color: 'var(--mantine-color-gray-7)' } }}
        />
      </Stack>

      {/* Подгруппа: хранение — бинарный Switch (как оборудование), стоимость раскрывается под ним. */}
      <Stack gap="sm">
        {/* Хранение + стоимость на одной строке; стоимость неактивна при выключенном хранении (без прыжков). */}
        <Group align="center" wrap="wrap" gap="xl">
          <Switch
            checked={state.storageAvailable}
            onChange={(e) => set({ storageAvailable: e.currentTarget.checked })}
            label={t('app.sell.form.loading.storageYes')}
          />
          <Group gap="xs" align="center" wrap="nowrap">
            <Text size="sm" c={state.storageAvailable ? undefined : 'dimmed'}>
              {t('app.sell.form.loading.storagePrice')}
            </Text>
            <NumberInput
              aria-label={t('app.sell.form.loading.storagePrice')}
              value={state.storagePrice}
              onChange={(value) => set({ storagePrice: value === '' ? '' : Number(value) })}
              disabled={!state.storageAvailable}
              min={STORAGE_PRICE_MIN}
              max={RATE_MAX}
              allowNegative={false}
              decimalScale={2}
              clampBehavior="blur"
              w={130}
            />
          </Group>
        </Group>
      </Stack>
    </Stack>
  );
}
